import { Clipboard } from '@angular/cdk/clipboard';
import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { displayTraceId } from '@core/interceptors/trace-context';
import { TranslatePipe } from '@ngx-translate/core';

/** The test ids of one box; the QA doc names them per box (docs features/booking-special-requests/qa.md § Test ids). */
export interface SpecialRequestsErrorBoxIds {
  box: string;
  code: string;
  trace: string;
  copy?: string;
  retry?: string;
}

/**
 * One coded error of booking special requests (rules C3 / C17 / C23): the translated message, the code, a copyable **Trace ID** and an
 * optional **Try again** ("Trying again…" while it runs). `role="alert"` so a screen reader announces it.
 */
@Component({
  selector: 'app-special-requests-error-box',
  imports: [TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="sr-err" role="alert" [attr.data-testid]="ids().box">
      <p class="sr-err-text">{{ message() | translate }}</p>
      <div class="sr-err-meta">
        <span>
          {{ 'Error code' | translate }}:
          <code [attr.data-testid]="ids().code">{{ code() }}</code>
        </span>
        @if (trace(); as id) {
          <span class="sr-err-trace">
            {{ 'Trace ID' | translate }}:
            <code [attr.data-testid]="ids().trace">{{ id }}</code>
            @if (ids().copy; as copyId) {
              <button
                type="button"
                class="sr-err-btn"
                [attr.data-testid]="copyId"
                [attr.aria-label]="'Copy Trace ID to clipboard' | translate"
                (click)="copy(id)"
              >
                {{ (copied() ? 'Copied' : 'Copy') | translate }}
              </button>
            }
          </span>
        }
      </div>
      @if (ids().retry; as retryId) {
        <div class="sr-err-actions">
          <button
            type="button"
            class="sr-err-btn sr-err-retry"
            [attr.data-testid]="retryId"
            [disabled]="retrying()"
            (click)="retry.emit()"
          >
            {{ 'Try again' | translate }}
          </button>
          @if (retrying()) {
            <span class="sr-err-retrying" role="status" [attr.data-testid]="retryId + 'ing'">{{
              'Trying again…' | translate
            }}</span>
          }
        </div>
      }
    </div>
  `,
  styles: `
    .sr-err {
      display: flex;
      flex-direction: column;
      gap: 0.375rem;
      padding: 0.625rem 0.75rem;
      border: 1px solid var(--kz-color-danger);
      border-left-width: 4px;
      border-radius: 4px;
      background: var(--c-ground);
      color: var(--kz-color-text);
      font-size: 0.8125rem;
      min-width: 0;
    }
    .sr-err-text {
      margin: 0;
      overflow-wrap: anywhere;
    }
    .sr-err-meta {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 0.25rem 0.75rem;
      font-size: 0.75rem;
      color: var(--kz-color-text-secondary);
    }
    .sr-err-meta code {
      font-size: 0.72rem;
      word-break: break-all;
      color: var(--kz-color-text);
    }
    .sr-err-trace {
      display: inline-flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 0.375rem;
      min-width: 0;
    }
    .sr-err-btn {
      border: 1px solid var(--kz-palette-light-blue-500);
      background: var(--c-ground);
      border-radius: 4px;
      padding: 0.0625rem 0.5rem;
      min-height: 1.75rem;
      font-size: 0.75rem;
      color: var(--kz-color-text);
      cursor: pointer;
    }
    .sr-err-btn:focus-visible {
      outline: 2px solid var(--kz-palette-light-blue-500);
      outline-offset: 2px;
    }
    .sr-err-actions {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 0.5rem;
    }
    .sr-err-retry {
      min-height: 2.75rem;
      padding: 0 0.875rem;
    }
    .sr-err-retrying {
      font-size: 0.75rem;
      color: var(--kz-color-text-secondary);
    }
  `
})
export class SpecialRequestsErrorBoxComponent {
  readonly #clipboard = inject(Clipboard);

  readonly ids = input.required<SpecialRequestsErrorBoxIds>();
  /** The English text = the translation key (C21). */
  readonly message = input.required<string>();
  readonly code = input.required<string>();
  readonly traceId = input<string | null | undefined>(null);
  readonly retrying = input(false);
  readonly retry = output<void>();

  readonly trace = computed(() => displayTraceId(this.traceId()));
  readonly copied = signal(false);

  copy(id: string): void {
    this.copied.set(this.#clipboard.copy(id));
  }
}
